import React, { ReactNode } from 'react';

import PropTypes from 'prop-types';

import { Container, useForwardedRef } from '@adsk/alloy-react-helpers';
import { XIcon } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

export type TokenProps = Omit<StylableComponent<HTMLDivElement>, 'content'> & {
  content?: ReactNode;
  disabled?: boolean;
  container?: Container;
  onClickX?: () => void;
};

const Token = React.forwardRef<HTMLDivElement, TokenProps>(
  (
    { className, style, disabled, content, container, onClickX, ...props },
    ref,
  ) => {
    const tokenRef = useForwardedRef(ref);
    return (
      <div
        ref={tokenRef}
        className={className}
        data-testid={'token'}
        css={[
          {
            display: 'flex',
            height: 24,
            alignItems: 'center',
            padding: `${theme.spacing.xxxSmall}px ${theme.spacing.xSmall}px`,
            margin: theme.spacing.xxSmall,
            borderRadius: theme.borderRadius.medium,
            background: theme.colors.charcoal100,
            color: disabled ? theme.colors.charcoal500 : 'inherit',
            cursor: disabled ? 'not-allowed' : 'inherit',
            userSelect: 'none',
          },
          theme.typography.bodyMedium,
          style,
        ]}
        {...props}
      >
        <OverflowTooltip
          singleLine
          autoTipOnEllipsis
          target={tokenRef}
          container={container}
          getTargetProps={() => ({ style: { maxWidth: 172 } })}
        >
          {content}
        </OverflowTooltip>
        <XIcon
          size={16}
          color={disabled ? theme.colors.charcoal500 : theme.colors.charcoal700}
          style={{
            marginLeft: theme.spacing.xSmall,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!disabled) onClickX?.();
          }}
        />
      </div>
    );
  },
);

Token.displayName = 'Token';

Token.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Token content */
  content: PropTypes.node,
  /** If the token is unclickable */
  disabled: PropTypes.bool,
  /** The container */
  container: PropTypes.any,
  /** Function executed upon clicking the X icon */
  onClickX: PropTypes.func,
};

export default Token;
